import {
  Admin,
  Resource,
  EditGuesser,
  ShowGuesser,
  type AuthProvider,
  CustomRoutes
} from 'react-admin';
import { Navigate, Route } from 'react-router-dom';
import { Notification } from 'react-admin'
import LoginPage from '../pages/login/LoginPage.tsx';
import { MyLayout } from '../components/layout/Layout.tsx';
import { MembersTable } from '../components/tables/membersTable.tsx';
import { MembersTableCreate } from '../components/tables/membersTableCreate.tsx';
import { CommissionTable } from '../components/tables/commissionTable.tsx';
import { LocationTable } from '../components/tables/locationsTable.tsx';
import { RiderTable } from '../components/tables/riderTable.tsx';
import { theme } from '../core/theme/theme.ts';
import OrdersScreen from '../pages/orders/OrdersPage.tsx';
import { useTranslation } from 'react-i18next';
import dataProvider from '../core/dataProvider.ts';
import { CommissionCouriersTable } from '../components/tables/commissionCouriersTable.tsx';
import { MembersEditTable } from '../components/tables/membersEditTable.tsx';
import LocationsCreate from '../pages/locations/locationsCreatePage.tsx';
import { LocationItemsTable } from '../components/tables/locationItemsTable.tsx';
import LocationsCreateItem from '../pages/locations/locationsCreateItemPage.tsx';
import { CommissionCreateDialog } from "../components/dialogs/CommissionManagementDialog.tsx";
import { RiderPerformanceTable } from '../components/tables/riderPerformanceTable.tsx';
import { OrdersMapScreen } from '../pages/map/MapPage.tsx';
import { LocationCategoryTable } from '../components/tables/locationCategoryTable.tsx';
import SupportScreen from '../pages/support/SupportPage.tsx';
import SettingsPage from '../pages/settings/SettingsPage.tsx';
import CreditReportPage from '../pages/credit/CreditReportPage.tsx';
import OrderHistoryPage from '../pages/orders/OrderHistoryPage.tsx';
import DeliveryPriorityPage from '../pages/delivery/DeliveryPriorityPage.tsx';
import NotificationsPage from '../pages/notifications/NotificationsPage.tsx';
import WhereCustomersPage from '../pages/notifications/WhereCustomersPage.tsx';
import PushQueuePage from '../pages/notifications/PushQueuePage.tsx';
import ShabbatMealsPage from '../pages/shabbat/ShabbatMealsPage.tsx';
import ShabbatRegistrationsPage from '../pages/shabbat/ShabbatRegistrationsPage.tsx';
import ShabbatGuestListPage from '../pages/shabbat/ShabbatGuestListPage.tsx';
import AttractionsCataloguePage from '../pages/attractions/AttractionsCataloguePage.tsx';
import AttractionsCollectionsPage from '../pages/attractions/AttractionsCollectionsPage.tsx';
import AttractionsRulesPage from '../pages/attractions/AttractionsRulesPage.tsx';
import AttractionsBookingsPage from '../pages/attractions/AttractionsBookingsPage.tsx';
import { PartnerCreate, PartnerEdit, PartnersList, PARTNERS_RESOURCE } from '../pages/marketing/PartnersPages.tsx';
import { DealCreate, DealEdit, DealsList, DEALS_RESOURCE } from '../pages/marketing/DealsPages.tsx';
import PartnerStatementPage from '../pages/marketing/PartnerStatementPage.tsx';
import AcquisitionReportPage from '../pages/marketing/AcquisitionReportPage.tsx';
import { isAdmin } from '../core/roles.ts';
import { forgetShabbatOnly, isShabbatOnlyLogin } from '../core/shabbatOnly.ts';
import { useState } from 'react';
import { SupportNotificationProvider } from '../context/SupportNotificationContext.tsx';
// import fakeRestProvider from 'ra-data-fakerest';
// import { fakeData } from '../mocks/index.ts';
// const fakeProvider = fakeRestProvider(fakeData, true);

const authProvider: AuthProvider = {
  login: async (_params) => {
    localStorage.setItem('auth', 'true');
    const { token, uid } = _params || {};
    localStorage.setItem('ra_token', token);
    localStorage.setItem('ra_uid', uid || '');
    return Promise.resolve();
  },
  logout: async () => {
    localStorage.removeItem('auth');
    localStorage.removeItem('ra_token');
    localStorage.removeItem('ra_uid');
    localStorage.removeItem('ra_role');
    forgetShabbatOnly();
    return Promise.resolve();
  },
  checkAuth: async () => {
    return localStorage.getItem('auth') ? Promise.resolve() : Promise.reject();
  },
  checkError: async () => Promise.resolve(),
  getPermissions: async () => Promise.resolve(),
};

// A Chabad House opens on its Shabbat meals: it has no orders to land on.
const ShabbatHome = () => <Navigate to="/shabbat/meals" replace />;

const MyNotification = () => (
  <Notification anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />
);

function App() {
  const { t } = useTranslation();
  const isVendor = localStorage.getItem("ra_role") === "VENDOR";
  // A Chabad House login (venue "Shabbat only"): Shabbat pages and its own
  // venue's details only. It never takes orders, so no orders, past orders,
  // menu or map. See core/shabbatOnly.ts.
  const shabbatOnly = isShabbatOnlyLogin();

  return (
    <SupportNotificationProvider>
      <Admin
        dataProvider={dataProvider}
        // dataProvider={fakeProvider}
        theme={theme}
        authProvider={authProvider}
        notification={MyNotification}
        loginPage={LoginPage}
        layout={MyLayout}
        dashboard={shabbatOnly ? ShabbatHome : undefined}>
        {!isVendor && <Resource
          options={{ label: t('resources.members') }}
          name="members"
          list={MembersTable}
          edit={MembersEditTable}
          create={MembersTableCreate}
          show={ShowGuesser}
        />}
        {!isVendor && <Resource
          options={{ label: t('resources.commissionManagement') }}
          name="commissionManagement"
          list={CommissionTable}
          create={CommissionCreateDialog}
          edit={EditGuesser}
          show={ShowGuesser}
        />}
        <Resource
          options={{ label: shabbatOnly ? t('resources.myVenue', 'My venue') : t('resources.locationsManagement') }}
          name="locationsManagement"
          create={LocationsCreate}
          list={LocationTable}
          edit={LocationsCreate}
          show={ShowGuesser}
        />
        {!shabbatOnly && <Resource
          options={{ label: t('resources.orders') }}
          name="orders"
          list={OrdersScreen}
        />}
        {!isVendor && <Resource
          options={{ label: t('resources.riderManagement') }}
          name="riderManagement"
          list={RiderTable}
          create={MembersTableCreate}
          edit={EditGuesser}
          show={ShowGuesser}
        />}
        {/* Marketing › Partners: hotels, hosts and others who send us customers.
            Admins only; the backend enforces the same on /admin/partners. The
            menu lists it under Marketing rather than with the other resources. */}
        {isAdmin() && <Resource
          options={{ label: t('resources.partners') }}
          name={PARTNERS_RESOURCE}
          list={PartnersList}
          create={PartnerCreate}
          edit={PartnerEdit}
        />}
        {/* Deals: thevello.com/d/<CODE> links (4 Nov 2026). Admins only, under Marketing. */}
        {isAdmin() && <Resource
          options={{ label: t('resources.deals') }}
          name={DEALS_RESOURCE}
          list={DealsList}
          create={DealCreate}
          edit={DealEdit}
        />}
        <CustomRoutes>
          {!isVendor &&
            <>
              <Route path="/commission/:id/couriers" element={<CommissionCouriersTable />} />
              <Route path="/riderManagement/:id/performance" element={<RiderPerformanceTable />} />
            </>
          }
          {!shabbatOnly &&
            <>
              <Route path="/locationsManagement/:id/items" element={<LocationItemsTable />} />
              <Route path="/locationsManagement/:id/items/create" element={<LocationsCreateItem />} />
              <Route path="/locationsManagement/:id/items/:itemId" element={<LocationsCreateItem />} />
              <Route path="/locationManagement/:id/categories" element={<LocationCategoryTable />} />
            </>
          }
          {!isVendor && <Route path="/support" element={<SupportScreen />} />}
          {/* Admins only, checked positively: !isVendor would hand the support
              number to any role added later. The backend enforces the same on
              the PATCH; this only decides whether the route exists. */}
          {isAdmin() && <Route path="/settings" element={<SettingsPage />} />}
          {isAdmin() && <Route path="/credit" element={<CreditReportPage />} />}
          {/* Order history (23 Oct 2026): Today's orders and Past orders in one page. Admins and
              restaurants alike; the server decides which orders each sees and gives restaurants
              the food total only. The two old addresses lead to it. */}
          {!shabbatOnly && <Route path="/orders/history" element={<OrderHistoryPage />} />}
          {!shabbatOnly && <Route path="/orders/today" element={<Navigate to="/orders/history" replace />} />}
          {!shabbatOnly && <Route path="/orders/past" element={<Navigate to="/orders/history" replace />} />}
          {isAdmin() && <Route path="/delivery-priority" element={<DeliveryPriorityPage />} />}
          {isAdmin() && <Route path="/notifications" element={<NotificationsPage />} />}
          {/* Where the customers' phones are, by country. Admins only like Notifications;
              the backend enforces the same on /admin/devices/where. */}
          {isAdmin() && <Route path="/notifications/where" element={<WhereCustomersPage />} />}
          {/* The push queue: what is waiting, what went out, what was dropped and why. */}
          {isAdmin() && <Route path="/notifications/queue" element={<PushQueuePage />} />}
          {/* Attractions (Viator curation): admins only, like Settings. The
              backend enforces the same on every /admin/attractions endpoint. */}
          {isAdmin() && <Route path="/attractions" element={<Navigate to="/attractions/catalogue" replace />} />}
          {isAdmin() && <Route path="/attractions/catalogue" element={<AttractionsCataloguePage />} />}
          {isAdmin() && <Route path="/attractions/collections" element={<AttractionsCollectionsPage />} />}
          {isAdmin() && <Route path="/attractions/rules" element={<AttractionsRulesPage />} />}
          {isAdmin() && <Route path="/attractions/bookings" element={<AttractionsBookingsPage />} />}
          {/* Marketing: where customers come from and what partners earn. Admins only;
              the backend enforces the same on /admin/partners and /admin/acquisition. */}
          {isAdmin() && <Route path="/marketing" element={<Navigate to={`/${PARTNERS_RESOURCE}`} replace />} />}
          {isAdmin() && <Route path="/marketing/partner-statement" element={<PartnerStatementPage />} />}
          {isAdmin() && <Route path="/marketing/where-from" element={<AcquisitionReportPage />} />}
          {/* Shabbat meals, for restaurants and admins alike. The pages ask the
              server which venues this login runs meals for and say so when it
              is none; the server holds a restaurant to its own venue. */}
          <Route path="/shabbat/meals" element={<ShabbatMealsPage />} />
          <Route path="/shabbat/registrations" element={<ShabbatRegistrationsPage />} />
          <Route path="/shabbat/guest-list" element={<ShabbatGuestListPage />} />
          {!shabbatOnly && <Route path="/orders/showMap" element={<OrdersMapScreen />} />}
          <Route path="/change-password" element={<OrdersMapScreen />} />
        </CustomRoutes>
      </Admin>
    </SupportNotificationProvider>
  );
}

export default App;